/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {PlatformDeviceType} from '@ion/core/src/types/entities';
import useDeviceTypes from '@ion/query/src/hooks/useDeviceTypes';

export const SUPPORTS_AUTOJOBS_FLAG = 'updateAssignedDevices';

export type UseSupportedAutojobFileTypesProps = {
  enabled?: boolean;
};

/**
 * Returns the device file types which support AutoJobs.
 *
 * @param   devicesTypes the device types to evaluate
 * @returns the file types which support AutoJobs
 */
export function getSupportedFileTypes(devicesTypes: PlatformDeviceType[]): string[] {
  const supportingFileTypes: string[] = [];
  devicesTypes?.forEach?.(deviceType => {
    deviceType?.fileTypes?.forEach?.(fileType => {
      const hasUpdateAssignedDevicesField = fileType?.inputFields?.some?.(
        field => field?.name === SUPPORTS_AUTOJOBS_FLAG
      );
      if (hasUpdateAssignedDevicesField) {
        supportingFileTypes.push(`${deviceType?.name} ${fileType?.name}`);
      }
    });
  });
  return supportingFileTypes;
}

/**
 * @useSupportedAutojobFileTypes
 * Defines the hook that returns an array of file types that support AutoJobs.
 * 
 * ```typescript
 * import useSupportedAutojobFileTypes from './useSupportedAutojobFileTypes';
 * 
 * const supportedAutojobFileTypes = useSupportedAutojobFileTypes({enabled: true});
 * 
 * return (
 *   <div>
 *     {supportedAutojobFileTypes.map(fileType => (
 *       <div key={fileType}>
 *         {fileType}
 *       </div>
 *     ))}
 *   </div>
 * 
 * ```
 *
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @returns the file types which support AutoJobs in an array.
 */
const useSupportedAutojobFileTypes = ({enabled = false}: UseSupportedAutojobFileTypesProps) => {
  const {deviceTypes} = useDeviceTypes(enabled, 60);
  return getSupportedFileTypes(deviceTypes);
};

export default useSupportedAutojobFileTypes;
